/**
 * AdvanceStageModal - select a new pipeline stage and optional notes.
 */
import React, { useState } from 'react';
import { Loader2, X, ArrowRight } from 'lucide-react';
import { PipelineStage } from '../types/dealPipeline';
import { PIPELINE_STAGES, STAGE_CONFIG } from './PipelineStageBadge';

interface Props {
  open: boolean;
  currentStage: PipelineStage;
  onClose: () => void;
  onConfirm: (newStage: PipelineStage, notes?: string) => Promise<void> | void;
}

const AdvanceStageModal: React.FC<Props> = ({
  open,
  currentStage,
  onClose,
  onConfirm,
}) => {
  const [newStage, setNewStage] = useState<PipelineStage>(() => {
    const idx = PIPELINE_STAGES.indexOf(currentStage);
    return PIPELINE_STAGES[Math.min(idx + 1, PIPELINE_STAGES.length - 1)];
  });
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onConfirm(newStage, notes.trim() || undefined);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Advance Stage</h2>
            <p className="text-xs text-gray-600 mt-1">
              Currently in <span className="font-medium">{STAGE_CONFIG[currentStage].label}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            New Stage
          </label>
          <select
            value={newStage}
            onChange={(e) => setNewStage(e.target.value as PipelineStage)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {PIPELINE_STAGES.map((s) => (
              <option key={s} value={s} disabled={s === currentStage}>
                {STAGE_CONFIG[s].label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Why are you moving this deal?"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || newStage === currentStage}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            Advance
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvanceStageModal;
